/**
 * Risk Analysis Module
 * Gestiona el análisis dinámico de riesgo de cuentas del usuario
 */

class RiskAnalyzer {
    constructor() {
        this.accounts = [];
        this.riskScores = {};
        // Por defecto mostramos "Todas"
        this.activeRiskFilter = 'all';
        this.activeRiskStatusFilter = 'all';
        this.init();
    }

    init() {
        // Esperar a que las cuentas se carguen desde accounts.js
        this.waitForAccounts();
    }

    waitForAccounts() {
        // Verificar si la variable global 'accounts' está disponible
        if (window.accounts && Array.isArray(window.accounts) && window.accounts.length > 0) {
            this.accounts = window.accounts;
            this.calculateRiskScores();
            this.renderRiskAnalysis();
            this.setupEventListeners();
        } else {
            // Reintentar cada 500ms hasta que las cuentas se carguen
            setTimeout(() => this.waitForAccounts(), 500);
        }
    }

    calculateRiskScores() {
        this.riskScores = {
            healthy: 0,
            warning: 0,
            danger: 0,
            scores: [],
            beFlags: []
        };

        this.accounts.forEach(account => {
            const isBE = this.isBreakEven(account);
            const score = isBE ? 0 : this.calculateAccountScore(account);
            this.riskScores.scores.push(score);
            this.riskScores.beFlags.push(isBE);

            // Si está en BE, lo consideramos neutral/ok (no alerta)
            if (isBE) {
                this.riskScores.healthy++;
            } else if (score >= 7.5) {
                this.riskScores.healthy++;
            } else if (score >= 5.0) {
                this.riskScores.warning++;
            } else {
                this.riskScores.danger++;
            }
        });
    }

    isBreakEven(account) {
        const metrics = account?.metrics || {};
        const totalTrades = Number(metrics.totalTrades) || 0;
        const totalProfit = Number(metrics.totalProfit) || 0;
        const roi = Number(metrics.roi) || 0;

        // BE si no hay trades (cuenta "plana")
        if (totalTrades === 0) return true;

        // BE si hay trades pero resultado neto es ~0
        return Math.abs(totalProfit) < 0.01 && Math.abs(roi) < 0.05;
    }

    getROIObjectiveByType(account) {
        // Usar objetivo personalizado si existe
        if (account.roi_objective) {
            return account.roi_objective;
        }

        // Sino, usar defaults según tipo
        const accountType = account.type || '';
        if (accountType.includes('challenge_f1')) return 10;
        if (accountType.includes('challenge_f2')) return 5;
        
        // Para fondeada y otros: sin objetivo fijo (null)
        return null;
    }

    getAccountTypeDisplay(accountType) {
        const typeMap = {
            'challenge_f1': { short: 'F1', full: 'Challenge F1' },
            'challenge_f2': { short: 'F2', full: 'Challenge F2' },
            'fondeada': { short: 'FD', full: 'Cuenta Fondeada' },
            'personal': { short: 'PE', full: 'Cuenta Personal' },
            'funded': { short: 'FD', full: 'Cuenta Fondeada' }
        };
        
        const normalizedType = (accountType || '').toLowerCase().replace(/ /g, '_');
        return typeMap[normalizedType] || { short: accountType, full: accountType };
    }

    getMaxDrawdownByType(accountType) {
        const typeMap = {
            'challenge_f1': 10,      // 10% máximo
            'challenge_f2': 5,       // 5% máximo
            'funded': 10,            // 10% máximo (relativo)
            'personal': Infinity     // Sin límite (quemar cuenta completa)
        };
        
        // Normalizar el tipo de cuenta
        const normalizedType = (accountType || '').toLowerCase().replace(/ /g, '_');
        return typeMap[normalizedType] || 10; // Default 10%
    }

    calculateAccountScore(account) {
        let score = 5.0; // Base score
        
        // Obtener métricas
        const metrics = account.metrics || {};
        const drawdown = Math.abs(parseFloat(metrics.drawdown) || 0);
        const winRate = parseFloat(metrics.winRate) || 0;
        const profitFactor = parseFloat(metrics.profitFactor) || 1.0;
        const roi = parseFloat(metrics.roi) || 0;
        const totalProfit = parseFloat(metrics.totalProfit) || 0;
        
        // Obtener el DD máximo según el tipo de cuenta
        const maxDrawdown = this.getMaxDrawdownByType(account.type);
        const ddRatio = maxDrawdown === Infinity ? 0 : drawdown / maxDrawdown;
        
        // Drawdown impact (basado en el máximo permitido)
        if (ddRatio < 0.2) score += 2;           // Menos del 20% del máximo = Excelente
        else if (ddRatio < 0.5) score += 1;      // 20-50% del máximo = Bien
        else if (ddRatio > 0.8) score -= 2;      // Más del 80% del máximo = Peligro

        // Win rate impact
        if (winRate > 60) score += 2;
        else if (winRate > 50) score += 1;
        else if (winRate < 40) score -= 1.5;

        // Profit factor impact
        if (profitFactor > 2.0) score += 1.5;
        else if (profitFactor > 1.5) score += 0.5;
        else if (profitFactor < 0.8) score -= 2;

        // ROI/Profit impact
        if (roi > 20) score += 0.5;
        else if (roi < -10) score -= 1;

        // Total profit impact (si hay ganancias, es más seguro)
        if (totalProfit > 0) score += 0.5;

        // ROI Objective bonus - alcanzar el target es MUY positivo
        const profitObjective = this.getROIObjectiveByType(account);
        if (profitObjective) {
            const roiProgress = roi / profitObjective;
            if (roiProgress >= 1.0) {
                score += 3; // Objetivo alcanzado → gran bonus
            } else if (roiProgress >= 0.8) {
                score += 1.5; // Cerca del objetivo → bonus moderado
            }
        }

        return Math.max(0, Math.min(10, parseFloat(score.toFixed(1))));
    }

    getRiskStatus(score, isBE) {
        if (isBE) return { status: 'BREAKEVEN', color: 'cyan', icon: '⏸' };
        if (score >= 7.5) return { status: 'SALUD EXCELENTE', color: 'green', icon: '✓' };
        if (score >= 5.0) return { status: '⚠️ PRECAUCIÓN', color: 'yellow', icon: '⚠️' };
        return { status: '🚨 ALTO RIESGO', color: 'red', icon: '🚨' };
    }

    getRiskColor(status) {
        if (status.color === 'green') return 'green';
        if (status.color === 'yellow') return 'yellow';
        return 'red';
    }

    formatCurrency(value) {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD'
        }).format(value || 0);
    }

    getColorClasses(color) {
        const classes = {
            green: {
                bg: 'bg-green-500/10 to-emerald-500/10',
                border: 'border-green-700/30 hover:border-green-500/50',
                badge: 'bg-green-500/20 border-green-500/40 text-green-400',
                text: 'text-green-400'
            },
            cyan: {
                bg: 'bg-cyan-500/10 to-blue-500/10',
                border: 'border-cyan-700/30 hover:border-cyan-500/50',
                badge: 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300',
                text: 'text-cyan-300'
            },
            yellow: {
                bg: 'bg-yellow-500/10 to-orange-500/10',
                border: 'border-yellow-700/30 hover:border-yellow-500/50',
                badge: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400',
                text: 'text-yellow-400'
            },
            red: {
                bg: 'bg-red-500/10 to-rose-500/10',
                border: 'border-red-700/30 hover:border-red-500/50',
                badge: 'bg-red-500/20 border-red-500/40 text-red-400',
                text: 'text-red-400'
            }
        };
        return classes[color];
    }

    getIconGradient(colorClass) {
        if (colorClass === 'cyan') return 'from-cyan-500 to-blue-500';
        if (colorClass === 'green') return 'from-green-500 to-emerald-500';
        if (colorClass === 'yellow') return 'from-yellow-500 to-orange-500';
        return 'from-red-500 to-rose-500';
    }

    renderRiskAnalysis() {
        // (Cards resumen eliminadas en UI) -> no actualizamos contadores si no existen
        const healthyEl = document.getElementById('healthyCount');
        const warningEl = document.getElementById('warningCount');
        const dangerEl = document.getElementById('dangerCount');
        const avgEl = document.getElementById('avgScore');
        if (healthyEl) healthyEl.textContent = this.riskScores.healthy;
        if (warningEl) warningEl.textContent = this.riskScores.warning;
        if (dangerEl) dangerEl.textContent = this.riskScores.danger;
        if (avgEl) {
            const scored = this.riskScores.scores.filter((_, idx) => !this.riskScores.beFlags[idx]);
            const avgScore = scored.length > 0
                ? (scored.reduce((a, b) => a + b, 0) / scored.length).toFixed(1)
                : '0.0';
            avgEl.textContent = avgScore;
        }

        // Render account items
        const container = document.getElementById('riskAccountsList');
        container.innerHTML = '';

        if (this.accounts.length === 0) {
            container.innerHTML = `
                <div class="text-center py-8 text-slate-400">
                    <p>No hay cuentas registradas. Agrega una cuenta para ver el análisis de riesgo.</p>
                </div>
            `;
            const prevBtn = document.getElementById('riskScrollPrev');
            const nextBtn = document.getElementById('riskScrollNext');
            if (prevBtn) prevBtn.style.display = 'none';
            if (nextBtn) nextBtn.style.display = 'none';
            return;
        }

        for (let index = 0; index < this.accounts.length; index++) {
            const account = this.accounts[index];
            const score = this.riskScores.scores[index];
            const isBE = !!this.riskScores.beFlags[index];
            const riskStatus = this.getRiskStatus(score, isBE);
            const colorClass = riskStatus.color;
            const colors = this.getColorClasses(colorClass);

            const riskLevel = isBE
                ? 'be'
                : (score >= 7.5 ? 'excellent' : (score >= 5.0 ? 'warning' : 'danger'));

            const metrics = account.metrics || {};
            const profitLoss = parseFloat(metrics.totalProfit) || 0;
            const roi = parseFloat(metrics.roi) || 0;
            const profitColor = profitLoss >= 0 ? 'text-green-400' : 'text-red-400';

            const initialBalance = Number(account.initial_balance || account.balance || 0);
            const totalProfit = Number(metrics.totalProfit || 0);
            const ddRaw = initialBalance > 0 ? (totalProfit / initialBalance) * 100 : 0;
            const drawdown = ddRaw < 0 ? Math.abs(ddRaw) : 0;
            const winRate = parseFloat(metrics.winRate) || 0;
            const profitFactor = parseFloat(metrics.profitFactor) || 1.0;
            const behavior = account.behavior || {};
            const disciplineLabel = behavior.status === 'consistente' ? 'Consistente'
                : behavior.status === 'controlada' ? 'Controlada'
                : behavior.status === 'inestable' ? 'Inestable'
                : behavior.status === 'riesgo' ? 'Riesgo alto'
                : 'Sin datos';
            const disciplineClass = behavior.status === 'consistente'
                ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                : behavior.status === 'controlada'
                ? 'text-blue-300 bg-blue-500/10 border-blue-500/30'
                : behavior.status === 'inestable'
                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                : behavior.status === 'riesgo'
                ? 'text-red-300 bg-red-500/10 border-red-500/30'
                : 'text-slate-300 bg-slate-700/30 border-slate-600/30';
            const revengeValue = behavior?.hasTimeData ? behavior.revengeCount : 'N/A';

            // Calculate progress percentages usando el objetivo personalizado
            const profitObjective = this.getROIObjectiveByType(account);
            
            // Margen de seguridad: basado en el DD máximo personalizado
            const maxDrawdown = account.max_drawdown || this.getMaxDrawdownByType(account.type);
            const safetyMargin = maxDrawdown === Infinity 
                ? Math.max(0, 100 - drawdown)  // Personal: hasta 100% (quemar cuenta completa)
                : Math.max(0, ((maxDrawdown - drawdown) / maxDrawdown) * 100);  // Fondeadas/Challenges: relativo al máximo

            // Insight avanzado basado en métricas
            const ddRemaining = maxDrawdown === Infinity ? null : (maxDrawdown - drawdown);
            const escalations = Number(behavior.escalations || 0);
            const lossEscalations = Number(behavior.lossEscalations || 0);
            const revengeCount = Number(behavior.revengeCount || 0);
            const consistency = behavior.consistencyLabel || 'N/A';

            const insight = (() => {
                if (maxDrawdown !== Infinity && drawdown >= maxDrawdown) {
                    return 'Límite de drawdown superado. La cuenta debería pausarse.';
                }
                if (maxDrawdown !== Infinity && ddRemaining !== null && ddRemaining <= 1) {
                    return `Estás a ${Math.max(0, ddRemaining).toFixed(1)}% del DD máximo. Reduce riesgo.`;
                }
                if (profitObjective && roi >= profitObjective) {
                    return 'Objetivo alcanzado. Protege el resultado y evita sobreoperar.';
                }
                if (profitFactor < 1 && winRate < 40) {
                    return 'Win rate y profit factor bajos. Revisa la calidad de entradas.';
                }
                if (profitFactor < 1 && roi < 0) {
                    return 'Profit factor bajo con ROI negativo. Reduce exposición.';
                }
                if (revengeCount > 0) {
                    return 'Señales de impulsividad: re-entradas rápidas tras pérdida. Pausa y vuelve al plan.';
                }
                if (lossEscalations > 0) {
                    return 'Aumentos de lote después de pérdidas. Reduce el tamaño y estabiliza el riesgo.';
                }
                if (behavior.status === 'riesgo') {
                    return 'Disciplina en riesgo. Baja exposición y vuelve a tu plan.';
                }
                if (escalations >= 2) {
                    return 'Escaladas frecuentes de lote. Estabiliza el tamaño.';
                }
                if (consistency === 'BAJA') {
                    return 'Consistencia de lotes baja. Uniforma el riesgo por trade.';
                }
                if (winRate >= 55 && profitFactor >= 1.5 && roi > 0) {
                    return 'Buen desempeño. Mantén la disciplina actual.';
                }
                if (roi < 0 && winRate < 45) {
                    return 'Racha negativa. Sé más selectivo y baja el riesgo.';
                }
                return 'Riesgo controlado. Mantén tu plan y el tamaño de posición.';
            })();

            const borderClass = colors.border;
            const gradientColor = this.getIconGradient(colorClass);
            const iconSvg = colorClass === 'green' 
                ? '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>'
                : colorClass === 'yellow'
                ? '<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>'
                : colorClass === 'cyan'
                ? '<path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1 5h2v6h-2V7zm0 8h2v2h-2v-2z" />'
                : '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>';

            const accountHTML = `
                <div class="risk-card bg-slate-900/50 border ${borderClass} rounded-xl p-4 hover:border-opacity-75 transition-all group h-full flex flex-col flex-shrink-0 w-80" style="touch-action: pan-y;" data-account-id="${account.id}" data-risk-level="${riskLevel}">
                    <div class="flex items-center justify-between mb-3">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center flex-shrink-0">
                                <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    ${iconSvg}
                                </svg>
                            </div>
                            <div class="min-w-0">
                                <h4 class="font-bold text-white mb-1 truncate">${account.name || `Cuenta ${index + 1}`}</h4>
                                <div class="flex items-center gap-2 text-xs flex-wrap">
                                    <span class="px-2 py-0.5 ${colors.badge} border rounded-full font-semibold whitespace-nowrap">
                                        ${riskStatus.status}
                                    </span>
                                    <span class="text-slate-500">•</span>
                                    <span class="text-slate-400">${isBE ? 'BREAKEVEN' : `Score: ${score}/10`}</span>
                                </div>
                            </div>
                        </div>
                        <div class="text-right flex-shrink-0">
                            <!-- Balance y Profit removidos -->
                        </div>
                    </div>
                    
                    <!-- Insight rápido -->
                    <div class="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 mb-3">
                        <div class="text-xs text-indigo-200 font-semibold mb-1">Insight</div>
                        <div class="text-xs text-white">${insight}</div>
                    </div>

                    <!-- Ganancias y Pérdidas Cards -->
                    <div class="grid grid-cols-2 gap-2 mb-3">
                        <!-- Card Ganancias -->
                        <div class="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                            <div class="text-xs text-green-400 font-semibold mb-1">GANANCIAS</div>
                            <div class="text-sm text-white font-bold mb-1">
                                ${metrics.wins || 0}/${metrics.totalTrades || 0}
                            </div>
                            <div class="text-xs text-green-400 font-semibold">
                                +${((metrics.grossProfit / account.initial_balance) * 100).toFixed(2)}%
                            </div>
                        </div>
                        
                        <!-- Card Pérdidas -->
                        <div class="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                            <div class="text-xs text-red-400 font-semibold mb-1">PÉRDIDAS</div>
                            <div class="text-sm text-white font-bold mb-1">
                                ${(metrics.totalTrades || 0) - (metrics.wins || 0)}/${metrics.totalTrades || 0}
                            </div>
                            <div class="text-xs text-red-400 font-semibold">
                                -${((metrics.grossLoss / account.initial_balance) * 100).toFixed(2)}%
                            </div>
                        </div>
                    </div>


                    <!-- Disciplina / Impulsividad -->
                    <div class="grid grid-cols-2 gap-2 mb-3">
                        <div class="border rounded-lg p-2 ${disciplineClass}" title="Disciplina general basada en consistencia de lotes, escaladas y revenge trades.">
                            <div class="text-[11px] font-semibold">Disciplina</div>
                            <div class="text-xs font-bold">${disciplineLabel}</div>
                            <div class="text-[10px] opacity-80">Lotes y reacción</div>
                        </div>
                        <div class="bg-slate-800/60 border border-slate-700/60 rounded-lg p-2" title="Número de entradas realizadas dentro de los 15 minutos después de una pérdida.">
                            <div class="text-[11px] text-slate-400">Revenge ≤15m</div>
                            <div class="text-xs text-white font-bold">${revengeValue}</div>
                            <div class="text-[10px] text-slate-500">Tras pérdida</div>
                        </div>
                        <div class="bg-slate-800/60 border border-slate-700/60 rounded-lg p-2" title="Cantidad de trades donde el tamaño subió ≥1.5x respecto al anterior.">
                            <div class="text-[11px] text-slate-400">Escaladas</div>
                            <div class="text-xs text-white font-bold">${behavior.escalations ?? 0}</div>
                            <div class="text-[10px] text-slate-500">≥1.5x tamaño</div>
                        </div>
                        <div class="bg-slate-800/60 border border-slate-700/60 rounded-lg p-2" title="Variación del tamaño de lote. Alta = estable, baja = volátil.">
                            <div class="text-[11px] text-slate-400">Consistencia</div>
                            <div class="text-xs text-white font-bold">${behavior.consistencyLabel || 'N/A'}</div>
                            <div class="text-[10px] text-slate-500">Variación lotes</div>
                        </div>
                    </div>

                    <!-- Progress Bars -->
                    <div class="space-y-2 flex-grow">
                        ${roi >= 0 && profitObjective ? `
                        <div>
                            <div class="flex justify-between text-xs mb-1">
                                <span class="text-slate-400">ROI Objetivo</span>
                                <span class="${colors.text} font-semibold">${roi.toFixed(1)}% / ${profitObjective.toFixed(1)}%</span>
                            </div>
                            <div class="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div class="h-full bg-gradient-to-r ${gradientColor} rounded-full" style="width: ${Math.min(100, (roi / profitObjective) * 100)}%"></div>
                            </div>
                        </div>
                        ` : roi >= 0 ? `
                        <div>
                            <div class="flex justify-between text-xs mb-1">
                                <span class="text-slate-400">ROI Actual</span>
                                <span class="${colors.text} font-semibold">${roi.toFixed(1)}%</span>
                            </div>
                        </div>
                        ` : `
                        <div>
                            <div class="flex justify-between text-xs mb-1">
                                <span class="text-slate-400">ROI Objetivo</span>
                                <span class="text-red-400 font-semibold">En pérdida</span>
                            </div>
                            <div class="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div class="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full" style="width: 0%"></div>
                            </div>
                        </div>
                        `}
                        <div>
                            <div class="flex justify-between text-xs mb-1">
                                <span class="text-slate-400">DD Máximo Permitido</span>
                                <span class="${colors.text} font-semibold">${drawdown.toFixed(1)}% / ${maxDrawdown.toFixed(1)}%</span>
                            </div>
                            <div class="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <div class="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full" style="width: ${Math.min(100, (drawdown / maxDrawdown) * 100)}%"></div>
                            </div>
                        </div>
                    </div>
                    ${(() => {
                        const obj = this.getROIObjectiveByType(account);
                        const roiVal = parseFloat(metrics.roi) || 0;
                        if (obj && roiVal >= obj) {
                            return this.renderAlert('green-objective');
                        } else if (obj && roiVal >= obj * 0.8 && roiVal > 0) {
                            return this.renderAlert('green-near');
                        } else if (!isBE && colorClass !== 'green' && colorClass !== 'cyan') {
                            return this.renderAlert(colorClass);
                        }
                        return '';
                    })()}
                </div>
            `;

            container.innerHTML += accountHTML;
        }

        // Aplicar filtro actual (si existe) y luego setup de scroll
        this.applyRiskFilter();

        // Setup scroll buttons después de renderizar
        setupRiskAnalysisScrollButtons();
    }

    setupRiskFilterButtons() {
        const buttons = Array.from(document.querySelectorAll('[data-risk-filter]'));
        if (!buttons.length) return;

        const activeClass = 'px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 text-cyan-300 hover:border-cyan-500/50 transition-all';
        const baseClass = 'px-4 py-2 rounded-lg text-sm font-semibold bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-cyan-300 hover:border-cyan-500/30 transition-all';

        const setActiveStyles = () => {
            const fresh = Array.from(document.querySelectorAll('[data-risk-filter]'));
            fresh.forEach(btn => {
                const f = btn.getAttribute('data-risk-filter');
                const isActive = this.activeRiskFilter === f;
                btn.className = `risk-filter-btn ${isActive ? activeClass : baseClass}`;
            });
        };

        // Evitar duplicar listeners: clonar botones como hacemos con los scroll
        buttons.forEach(btn => {
            const clone = btn.cloneNode(true);
            btn.parentNode.replaceChild(clone, btn);
        });

        const freshButtons = Array.from(document.querySelectorAll('[data-risk-filter]'));
        freshButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const f = btn.getAttribute('data-risk-filter');
                // Si se vuelve a clicar el filtro activo, volvemos a "Todas"
                this.activeRiskFilter = (this.activeRiskFilter === f) ? 'all' : f;
                setActiveStyles();
                this.applyRiskFilter();
                setupRiskAnalysisScrollButtons();
            });
        });

        setActiveStyles();
    }

    setupRiskStatusFilterSelect() {
        const statusSelect = document.getElementById('riskStatusSelect');
        if (!statusSelect) return;
        
        statusSelect.addEventListener('change', () => {
            this.activeRiskStatusFilter = statusSelect.value;
            this.applyRiskFilter();
            setupRiskAnalysisScrollButtons();
        });
    }

    applyRiskFilter() {
        const container = document.getElementById('riskAccountsList');
        if (!container) return;

        const cards = Array.from(container.querySelectorAll('.risk-card'));
        if (!cards.length) return;

        const filter = this.activeRiskFilter;
        cards.forEach(card => {
            const level = card.getAttribute('data-risk-level') || '';
            
            // Aplicar filtro de nivel de riesgo
            let levelMatch = false;
            if (!filter || filter === 'all') {
                levelMatch = true;
            } else if (filter === 'excellent') {
                // Excelente incluye BE (neutral)
                levelMatch = (level === 'excellent' || level === 'be');
            } else {
                levelMatch = (level === filter);
            }
            
            if (!levelMatch) {
                card.style.display = 'none';
                return;
            }
            
            // Aplicar filtro de estado
            const statusFilter = this.activeRiskStatusFilter;
            if (statusFilter && statusFilter !== 'all') {
                const accountId = card.getAttribute('data-account-id');
                const account = this.accounts.find(a => a.id === accountId);
                if (!account) {
                    card.style.display = 'none';
                    return;
                }
                
                const status = account.status || 'active';
                
                // "active" incluye tanto active como inactive (cuentas operativas)
                if (statusFilter === 'active') {
                    if (status !== 'active' && status !== 'inactive') {
                        card.style.display = 'none';
                        return;
                    }
                } else {
                    // Otros estados (burned, superado)
                    if (status !== statusFilter) {
                        card.style.display = 'none';
                        return;
                    }
                }
            }
            
            card.style.display = '';
        });
    }

    renderAlert(color) {
        const alerts = {
            yellow: {
                bgColor: 'bg-yellow-500/10 border-yellow-500/30',
                textColor: 'text-yellow-300',
                message: 'Acercándose al límite de drawdown. Reduce el tamaño de posición.'
            },
            red: {
                bgColor: 'bg-red-500/10 border-red-500/30',
                textColor: 'text-red-300',
                message: 'Cuenta cerca del límite. Considera dejar de operar o reducir drasticamente el riesgo.'
            },
            'green-objective': {
                bgColor: 'bg-emerald-500/10 border-emerald-500/30',
                textColor: 'text-emerald-300',
                message: '🎯 ¡Objetivo alcanzado! Protege el resultado y considera dejar de operar esta cuenta.'
            },
            'green-near': {
                bgColor: 'bg-emerald-500/10 border-emerald-500/30',
                textColor: 'text-emerald-300',
                message: '🔥 Cerca del objetivo. Mantén la disciplina y no arriesgues lo ganado.'
            }
        };

        const alert = alerts[color];
        return `
            <div class="mt-3 p-2 ${alert.bgColor} border rounded-lg">
                <div class="flex items-start gap-2 text-xs">
                    <svg class="w-4 h-4 ${alert.textColor} flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                    </svg>
                    <span class="${alert.textColor}">${alert.message}</span>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        const reportBtn = document.getElementById('reportBtn');
        if (reportBtn) {
            reportBtn.addEventListener('click', () => this.generateReport());
        }

        this.setupRiskFilterButtons();
        this.setupRiskStatusFilterSelect();
    }

    generateReport() {
        if (window.showAlert) {
            window.showAlert('Próximamente', 'Función de reporte en desarrollo. Próximamente exportarás análisis en PDF.', 'info');
        }
    }
}

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
    const analyzer = new RiskAnalyzer();
    
    // Exposer la instancia globalmente para poder llamar refresh desde accounts.js
    window.riskAnalyzer = analyzer;
});

// Agregar método global para refrescar el análisis cuando cambien las cuentas
window.refreshRiskAnalysis = function() {
    if (window.riskAnalyzer) {
        window.riskAnalyzer.accounts = window.accounts || [];
        window.riskAnalyzer.calculateRiskScores();
        window.riskAnalyzer.renderRiskAnalysis();
        setupRiskAnalysisScrollButtons();
    }
};

/**
 * Setup scroll buttons para Risk Analysis (móvil)
 */
function setupRiskAnalysisScrollButtons() {
    const scrollContainer = document.getElementById('riskAnalysisScroll');
    const scrollPrevBtn = document.getElementById('riskScrollPrev');
    const scrollNextBtn = document.getElementById('riskScrollNext');
    
    if (!scrollContainer || !scrollPrevBtn || !scrollNextBtn) return;

    // Permitir arrastrar con el dedo/ratón para desplazar horizontalmente
    enableDragScrollX(scrollContainer);
    
    // Eliminar listeners anteriores para evitar duplicados
    const newPrevBtn = scrollPrevBtn.cloneNode(true);
    const newNextBtn = scrollNextBtn.cloneNode(true);
    scrollPrevBtn.parentNode.replaceChild(newPrevBtn, scrollPrevBtn);
    scrollNextBtn.parentNode.replaceChild(newNextBtn, scrollNextBtn);
    
    const prevBtn = document.getElementById('riskScrollPrev');
    const nextBtn = document.getElementById('riskScrollNext');
    
    const scrollAmount = 400;
    
    function updateButtonStates() {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        if (!isDesktop) {
            prevBtn.style.display = 'none';
            nextBtn.style.display = 'none';
            return;
        }

        const scrollLeft = scrollContainer.scrollLeft;
        const scrollWidth = scrollContainer.scrollWidth;
        const clientWidth = scrollContainer.clientWidth;

        const cards = scrollContainer.querySelectorAll('.risk-card');
        if (cards.length < 5) {
            prevBtn.style.display = 'none';
            nextBtn.style.display = 'none';
            return;
        }
        
        prevBtn.style.display = scrollLeft <= 0 ? 'none' : 'flex';
        nextBtn.style.display = (scrollLeft + clientWidth >= scrollWidth - 10) ? 'none' : 'flex';
    }
    
    prevBtn.addEventListener('click', () => {
        scrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        setTimeout(updateButtonStates, 300);
    });
    
    nextBtn.addEventListener('click', () => {
        scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        setTimeout(updateButtonStates, 300);
    });
    
    updateButtonStates();
    scrollContainer.addEventListener('scroll', updateButtonStates);
}

function enableDragScrollX(container) {
    if (!container) return;
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
    if (container.dataset && container.dataset.dragScrollBound === '1') return;
    if (container.dataset) container.dataset.dragScrollBound = '1';

    let isPointerDown = false;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let axisLock = null; // 'x' | 'y' | null
    let suppressClick = false;

    const threshold = 6;

    container.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        isPointerDown = true;
        axisLock = null;
        startX = e.clientX;
        startY = e.clientY;
        startScrollLeft = container.scrollLeft;
        suppressClick = false;

        try {
            container.setPointerCapture(e.pointerId);
        } catch {
            // ignore
        }
    }, { passive: true });

    container.addEventListener('pointermove', (e) => {
        if (!isPointerDown) return;

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (axisLock === null) {
            if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
            axisLock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }

        // Si el gesto es vertical, no interceptamos (permite scroll de página)
        if (axisLock === 'y') return;

        // Gesto horizontal: movemos el scroll del contenedor
        e.preventDefault();
        container.scrollLeft = startScrollLeft - dx;
        suppressClick = true;
    }, { passive: false });

    const end = (e) => {
        if (!isPointerDown) return;
        isPointerDown = false;
        axisLock = null;
        try {
            container.releasePointerCapture(e.pointerId);
        } catch {
            // ignore
        }
        if (suppressClick) {
            setTimeout(() => { suppressClick = false; }, 0);
        }
    };

    container.addEventListener('pointerup', end, { passive: true });
    container.addEventListener('pointercancel', end, { passive: true });

    // Evitar clicks accidentales tras arrastrar
    container.addEventListener('click', (e) => {
        if (!suppressClick) return;
        e.preventDefault();
        e.stopPropagation();
    }, true);
}
